import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalizationContext } from '@im/react-localization';
import { useLocation } from 'react-router';
import { UjetTO, useUjetAuth } from '../../hooks/useUjetAuth';
import { useIsUjetChatbotEnabled } from '../../hooks/useIsUjetChatbotEnabled';
import { injectScriptInHead } from './utils';
import { useDecodedToken } from 'mf_core/auth';
import { useBuildVars, useSiteConfig } from 'mf_core/config';
import { getLsObject } from '@im/ui-lib/utils/local-storage.utils';
import { ujetFrameScriptsMap } from './useAdobeDtm';

const baseUrl = globalThis.location.origin;

interface AccountResultTO {
  accountID?: string;
  companycode?: string;
  countryID?: string;
  currencyID?: string;
  ownerID?: string;
  priceList?: string;
}

/** CCAI Ujet Chatbot/Live chat */
export function useUjetChatBot() {
  const { locale, localizationService } = useLocalizationContext();
  const { buildEnvironment } = useBuildVars();
  const { environment, siteCode } = useSiteConfig();
  const langRef = useRef('');
  const { pathname } = useLocation();
  const [ujetTranslations, setUjetTranslations] = useState<{
    [key: string]: { [key: string]: string };
  }>({});

  const ujetTranslationsRef = useRef(ujetTranslations);

  const decodedToken = useDecodedToken();
  const firstName: string = decodedToken?.imoProfile?.firstName || '';
  const lastName: string = decodedToken?.imoProfile?.lastName || '';
  const bcn: string = decodedToken?.resellerId || '';
  const email: string = decodedToken?.email || '';
  const userId: string = decodedToken?.userId || '';
  const accountId = useMemo(() => {
    const accountResponse = getLsObject('getAccount', { data: {} });
    const accountData: AccountResultTO =
      accountResponse?.data || accountResponse;
    return accountData?.accountID || '';
  }, []);

  const {
    isEnabled,
    isValidPath,
    translationKeys: ujetKeys,
  } = useIsUjetChatbotEnabled(false);
  const { data: ujetConfig } = useUjetAuth(isEnabled, false);

  const UJET = window.UJET;
  const ujetInstanceRef = useRef(null as UJETInstance);

  const [isScriptInjected, setIsScriptInjected] = useState(false);
  const isAdobeScriptInjected = useRef(false);

  function hideUjet() {
    const ujet = ujetInstanceRef.current;
    if (ujet?.$el?.style) {
      ujet.$el.style.display = 'none';
    }
  }

  function showUjet() {
    const ujet = ujetInstanceRef.current;
    if (ujet?.$el?.style) {
      ujet.$el.style.display = 'block';
    }
  }

  useEffect(() => {
    langRef.current = locale.split('-')[0];
  }, [locale]);

  useEffect(() => {
    if (isEnabled) {
      if (!isScriptInjected && isValidPath) {
        injectScriptInHead('https://websdk.ujet.co/v2/loader.js', () => {
          setIsScriptInjected(true);
        });
      }
      //Close chatbot widget and hide chatbot launcher button when leaving Returns routes
      else if (isScriptInjected && !isValidPath) {
        hideUjet();
      }
      //Once injected, display chatbot launcher button when returning to a Returns route
      else if (isScriptInjected && isValidPath) {
        showUjet();
      }
    }
  }, [isEnabled, isScriptInjected, isValidPath, pathname]);

  const keys = useMemo(() => {
    return ujetKeys.map((key) => {
      return `Ujet.${key}`;
    });
  }, [ujetKeys]);

  const loadTranslations = useCallback(() => {
    if (localizationService && keys.length > 0) {
      localizationService
        .translate(keys, { returnKeyIfNotFound: false })
        .then((res: any) => {
          const langTranslations: { [key: string]: string } = {};
          ujetKeys.forEach((key) => {
            langTranslations[key] = res[`Ujet.${key}`] || '';
          });
          ujetTranslationsRef.current = {
            ...ujetTranslations,
            [langRef.current]: langTranslations,
          };
          setUjetTranslations(ujetTranslationsRef.current);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, localizationService, ujetKeys]);

  function destroyUjet() {
    if (ujetInstanceRef.current) {
      ujetInstanceRef.current.destroy();
      if (ujetInstanceRef.current.$el) {
        ujetInstanceRef.current.$el.remove();
      }
    }
  }

  function loadAdobeAnalytics() {
    if (!isAdobeScriptInjected.current) {
      const container = document.getElementById('ujet-launcher');
      const ujetIframe = container ? container.querySelector('iframe') : null;
      if (ujetIframe) {
        const iframeDocument =
          ujetIframe.contentDocument || ujetIframe.contentWindow?.document;
        if (iframeDocument) {
          const scriptId = getAdbodeScript();
          // Create a script element
          const script = iframeDocument.createElement('script');
          script.src = scriptId; // Set the source of your script
          script.async = true;

          const digitalDataScript = document.createElement('script');
          digitalDataScript.innerHTML = 'var digitalData = {};';
          // Append the script to the iframe document
          iframeDocument.head.appendChild(script);
          iframeDocument.head.appendChild(digitalDataScript);
          isAdobeScriptInjected.current = true;
        }
      }
    }
  }

  function getAdbodeScript() {
    let scriptId = '';
    if (buildEnvironment && siteCode) {
      const envMap = ujetFrameScriptsMap[buildEnvironment];
      if (envMap) {
        scriptId = envMap[siteCode];
      }
    }
    return scriptId;
  }

  function setupUjet(config: UjetTO) {
    // Initialize UJET web-sdk. For more info:
    // https://cloud.google.com/contact-center/ccai-platform/docs/API/publication--en

    const options: { [option: string]: any } = {
      authenticate: config.token,
      bottom: '140px',
      companyId: config.configurations.companyId,
      companyName: config.configurations.companyName,
      customData: {
        bcn: {
          label: 'bcn',
          value: bcn,
        },
        country: {
          label: 'country',
          value: siteCode,
        },
        email: {
          label: 'Email',
          value: email,
        },
        name: {
          label: 'name',
          value: `${firstName} ${lastName}`,
        },
        values: {
          label: 'values',
          value: {
            accountId,
            email,
            environment,
            language: locale,
            userId,
          },
        },
      },
      host: config.configurations.host,
      lang: langRef.current,
      launcher: true,
      logo: `${baseUrl}/cep/assets/img/chatbot/logo.png`,
    };

    if (Object.keys(ujetTranslationsRef.current).length > 0) {
      options.translation = ujetTranslationsRef.current;
    }

    if (config.configurations.defaultMenuKey) {
      options.menuKey = config.configurations.defaultMenuKey;

      setTimeout(() => {
        ujetInstanceRef.current = new UJET(options);

        if (ujetInstanceRef.current) {
          loadAdobeAnalytics();
          /** Ujet currently doesn't work second time if menuKey is used.
           * This is a hack to destroy and reconfigure ujet.]
           * This patch should be removed once menuKey issue is resolved.
           */
          ujetInstanceRef.current.on('close', () => {
            destroyUjet();
            setupUjet(config);
          });
        }
      }, 100);
    } else {
      ujetInstanceRef.current = new UJET(options);
    }
  }

  useEffect(() => {
    if (UJET && ujetConfig && isScriptInjected && !ujetInstanceRef.current) {
      if (ujetKeys.length > 0 && Object.keys(ujetTranslations).length === 0) {
        // load translations before initializing Ujet.
        loadTranslations();
        return;
      }
      setupUjet(ujetConfig);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptInjected, ujetTranslations, ujetConfig, UJET]);

  useEffect(() => {
    if (isEnabled && ujetInstanceRef.current && ujetKeys.length > 0) {
      // load translations on language change.
      if (!Object.keys(ujetTranslations).includes(langRef.current)) {
        hideUjet();
        loadTranslations();
      } else {
        ujetInstanceRef.current.options.lang = langRef.current;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, locale, ujetKeys]);

  useEffect(() => {
    if (isEnabled && ujetInstanceRef.current) {
      // update language and translations on language change.
      ujetInstanceRef.current.options.lang = langRef.current;
      ujetInstanceRef.current.options.translation = ujetTranslations;
      showUjet();
    }
  }, [isEnabled, ujetTranslations, ujetInstanceRef]);
}

declare global {
  interface Window {
    UJET: any;
  }
}

type UJETInstance = null | {
  $el: HTMLElement;
  close: () => void;
  destroy: () => void;
  on: (event: string, cb: () => void) => void;
  open: () => void;
  options: ConfigureOptions;
  start: (options?: StartOptions) => void;
  status: string;
};

type StartOptions = { [menuKey: string]: string };
type ConfigureOptions = { [key: string]: any };
